import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from './Button';
import { EmptyState } from './Feedback';

/**
 * A screen that breaks says so, instead of going blank.
 *
 * React unmounts the whole subtree when a render throws, so without one of
 * these the page simply empties — which is what the client reported as "it
 * sometimes goes blank". A blank screen tells nobody anything: not whether to
 * wait, not whether to reload, not whether to report it.
 *
 * Deliberately not clever. It does not retry by itself and it does not try to
 * recover the broken component — it says the screen stopped, offers the one
 * action that usually works, and leaves the error in the console where it can
 * be read.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; title?: string },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Left in the console on purpose: a blank screen with a silent error is
    // the thing being fixed here.
    console.error('Screen failed to render', error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;

    return (
      <EmptyState
        title={this.props.title ?? 'This screen stopped'}
        description="Something went wrong rendering it. Reloading usually brings it back — if it keeps happening, tell us what you were opening."
        action={
          <Button variant="outline" leadingIcon={<RefreshCw />} onClick={() => window.location.reload()}>
            Reload
          </Button>
        }
      />
    );
  }
}
